import React from 'react';
import { useTranslation } from 'react-i18next';

// Max days per month (non-leap year; Feb capped at 28 since no year context)
const DAYS_IN_MONTH = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

function maxDaysForMonth(month: number | ''): number {
  if (month === '') return 31;
  return DAYS_IN_MONTH[month - 1];
}

export interface MonthDayPickerProps {
  /** Visible label above the pickers */
  label?: string;
  /** Renders a red asterisk next to the label */
  required?: boolean;
  /** Selected month (1–12) or '' when not yet chosen */
  month: number | '';
  /** Selected day (1–31) or '' when not yet chosen */
  day: number | '';
  /** Called whenever either select changes */
  onChange: (_month: number | '', _day: number | '') => void;
  disabled?: boolean;
  /** Validation error message shown below the pickers */
  error?: string;
}

const selectCls =
  'w-full px-3 py-1.5 border border-gray-300 rounded-sm font-body text-sm bg-white text-gray-900 ' +
  'outline-none transition-agrobook duration-agrobook ' +
  'focus:border-green-600 focus:ring-4 focus:ring-green-600/15 ' +
  'disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed';

const errorSelectCls = 'border-red-400 focus:border-red-500 focus:ring-red-500/15';

export const MonthDayPicker: React.FC<MonthDayPickerProps> = ({
  label,
  required,
  month,
  day,
  onChange,
  disabled = false,
  error,
}) => {
  const { t } = useTranslation();

  function handleMonthChange(e: React.ChangeEvent<{ value: string }>) {
    const newMonth = e.target.value === '' ? '' : (Number(e.target.value) as number);
    const newMax = newMonth === '' ? 31 : DAYS_IN_MONTH[(newMonth as number) - 1];
    const clampedDay = day !== '' && day > newMax ? (newMax as number) : day;
    onChange(newMonth, clampedDay);
  }

  function handleDayChange(e: React.ChangeEvent<{ value: string }>) {
    const newDay = e.target.value === '' ? '' : (Number(e.target.value) as number);
    onChange(month, newDay);
  }

  const maxDays = maxDaysForMonth(month);
  const hasError = Boolean(error);

  return (
    <div className="flex flex-col gap-1.5">
      {label && (
        <label className="text-3xs font-semibold text-gray-700 tracking-wider">
          {label}
          {required && <span className="text-red-600 ms-0.5">*</span>}
        </label>
      )}
      <div className="flex gap-2">
        <select
          value={month}
          onChange={handleMonthChange}
          disabled={disabled}
          aria-label={label ? `${label} – month` : 'Month'}
          className={`w-[115px] ${selectCls} ${hasError ? errorSelectCls : ''}`}
        >
          <option value="">{t('monthDayPicker.monthPlaceholder')}</option>
          {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
            <option key={m} value={m}>
              {t(`monthDayPicker.months.${m}`)}
            </option>
          ))}
        </select>
        <select
          value={day}
          onChange={handleDayChange}
          disabled={disabled || month === ''}
          aria-label={label ? `${label} – day` : 'Day'}
          className={`w-[65px] ${selectCls} ${hasError ? errorSelectCls : ''}`}
        >
          <option value="">{t('monthDayPicker.dayPlaceholder')}</option>
          {Array.from({ length: maxDays }, (_, i) => i + 1).map(d => (
            <option key={d} value={d}>
              {d}
            </option>
          ))}
        </select>
      </div>
      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  );
};
